import React, { useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { investorApi, ownershipApi, vehicleApi } from '../../api/portal';
import usePortalQuery, { invalidatePortal } from '../../hooks/usePortalQuery';
import { QueryStatus } from '../../components/portal/PortalUI';
import { CostManager } from '../vehicleCosts/VehicleCosts';
import ProfileMerge from './ProfileMerge';
import Tabs, { useTabParam } from '../../components/portal/Tabs';
import VehicleEarnings from '../../components/portal/VehicleEarnings';

const normalize = (item) => item.profile || item;
const label = (profile) => profile.plate_number || profile.id;

export default function VehicleManagement() {
  const { profileId } = useParams();
  return profileId ? <VehicleProfile key={profileId} profileId={profileId} /> : <VehicleList />;
}

function VehicleList() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState(params.get('q') || '');
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [mergeBusy, setMergeBusy] = useState(false);
  const q = params.get('q') || '';
  const offset = Number(params.get('offset') || 0);
  const investorId = params.get('investor_id') || '';
  const unassigned = params.get('unassigned') === 'true';
  const filters = { q, offset, limit: 50, ...(investorId ? { investor_id: investorId } : {}), ...(unassigned ? { unassigned: true } : {}) };
  const list = usePortalQuery(['admin-vehicle-list', filters], (signal) => vehicleApi.list(filters, signal));
  const investors = usePortalQuery(['car-management-investors'], (signal) => investorApi.list('', signal));
  const profiles = (list.data?.vehicle_profiles || []).map(normalize);
  const changeFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('offset'); setParams(next);
  };
  const selectProfile = (profile) => setSelected((current) => current.some((item) => item.id === profile.id) ?
    current.filter((item) => item.id !== profile.id) : [...current, profile]);
  return <main className="portal"><header className="portal-header"><h1>Car management</h1>
    <p>Manage vehicle profiles, ownership, and additional staff costs.</p></header>
    <QueryStatus query={list} /><QueryStatus query={investors} />
    <section className="portal-card"><div className="portal-controls">
      <form className="portal-actions" onSubmit={(event) => { event.preventDefault(); changeFilter('q', search.trim()); }}>
        <label>Search cars<input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Plate, make, or model" /></label>
        <button>Search</button>
      </form>
      <label>Investor<select value={investorId} onChange={(event) => changeFilter('investor_id', event.target.value)}>
        <option value="">All owners</option>{investors.data?.map((investor) => <option key={investor.id} value={investor.id}>{investor.name}</option>)}
      </select></label>
      <label className="portal-check"><input type="checkbox" checked={unassigned} onChange={(event) => changeFilter('unassigned', event.target.checked ? 'true' : '')} />Unassigned only</label>
      <button type="button" onClick={() => setCreateOpen((value) => !value)}>{createOpen ? 'Close form' : 'Create car'}</button>
    </div></section>
    {createOpen && <CreateProfile onCreated={(id) => { setCreateOpen(false); invalidatePortal(); navigate(`/admin/vehicles/${encodeURIComponent(id)}`); }} />}
    {selected.length > 0 && <ProfileMerge profiles={selected} investors={investors.data || []}
      investorsLoading={investors.loading || !!investors.error}
      onSelectionChange={setSelected} onBusyChange={setMergeBusy}
      onMerged={(id) => { setSelected([]); navigate(`/admin/vehicles/${encodeURIComponent(id)}`); }} />}
    {list.data && (profiles.length ? <>
      <p className="portal-muted">Each car has one current owner. Select up to 21 profiles to merge into one surviving car.</p>
      <section className="portal-card"><div className="portal-table-scroll"><table>
        <caption>{list.data.count} matching cars</caption><thead><tr><th>Select for merge</th><th>Plate</th><th>Profile ID</th><th>Make / model</th><th>Year</th><th>Description</th><th>Current owner</th><th>Management</th></tr></thead>
        <tbody>{profiles.map((profile) => {
          const ownerId = profile.Owner_investor_id;
          const owner = investors.data?.find((entry) => entry.id === ownerId);
          const checked = selected.some((item) => item.id === profile.id);
          return <tr key={profile.id}><td><input type="checkbox" aria-label={`Select ${profile.id} for merge`} checked={checked}
            disabled={mergeBusy || (!checked && selected.length >= 21)} onChange={() => selectProfile(profile)} /></td><td>{label(profile)}</td>
            <td>{profile.id}</td>
            <td>{[profile.Make || profile.make, profile.Model || profile.model].filter(Boolean).join(' ') || '-'}</td>
            <td>{profile.year || '-'}</td><td>{profile.description || '-'}</td>
            <td>{owner?.name || ownerId || 'Unassigned'}</td>
            <td><Link to={`/admin/vehicles/${encodeURIComponent(profile.id)}`}>Manage car</Link></td></tr>;
        })}</tbody></table></div>
        <div className="portal-actions"><button disabled={offset <= 0} onClick={() => { const next = new URLSearchParams(params); next.set('offset', String(Math.max(0, offset - 50))); setParams(next); }}>Previous</button>
          <span>Showing {offset + 1}–{Math.min(offset + profiles.length, list.data.count)} of {list.data.count}</span>
          <button disabled={offset + profiles.length >= list.data.count} onClick={() => { const next = new URLSearchParams(params); next.set('offset', String(offset + 50)); setParams(next); }}>Next</button></div>
      </section>
    </> : <section className="portal-card"><p>{q ? 'No cars match your search.' : 'No cars found.'}</p></section>)}
  </main>;
}

function CreateProfile({ onCreated }) {
  const [fields, setFields] = useState({ plate_number: '', make: '', model: '', year: '', description: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const change = (key, value) => setFields((current) => ({ ...current, [key]: value }));
  const submit = async (event) => {
    event.preventDefault(); setError('');
    const year = fields.year ? Number(fields.year) : null;
    if (!fields.plate_number.trim()) { setError('Plate number is required.'); return; }
    if (fields.year && (!Number.isInteger(year) || year < 1886 || year > 2100)) { setError('Year must be an integer from 1886 to 2100.'); return; }
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true);
    try {
      const profile = await vehicleApi.create({
        plate_number: fields.plate_number.trim(), make: fields.make.trim() || null,
        model: fields.model.trim() || null, year, description: fields.description.trim() || null,
      });
      onCreated(profile.id);
    } catch (requestError) {
      setError(requestError.status === 409 ?
        `A car with this plate already exists. ${requestError.message} Keep this draft and search the fleet before retrying.` :
        requestError.message || 'Unable to create the car.');
    } finally { busyRef.current = false; setBusy(false); }
  };
  return <section className="portal-card"><h2>Create car profile</h2>{error && <p className="portal-error" role="alert">{error} <Link to="/admin/vehicles">Search cars</Link></p>}
    <form className="portal-form" onSubmit={submit}>
      <label>Plate number<input required value={fields.plate_number} onChange={(event) => change('plate_number', event.target.value)} /></label>
      <label>Make<input value={fields.make} onChange={(event) => change('make', event.target.value)} /></label>
      <label>Model<input value={fields.model} onChange={(event) => change('model', event.target.value)} /></label>
      <label>Year<input type="number" min="1886" max="2100" value={fields.year} onChange={(event) => change('year', event.target.value)} /></label>
      <label>Description<textarea value={fields.description} onChange={(event) => change('description', event.target.value)} /></label>
      <div className="portal-actions"><button disabled={busy}>{busy ? 'Creating...' : 'Create car'}</button></div>
    </form>
  </section>;
}

function VehicleProfile({ profileId }) {
  const details = usePortalQuery(['admin-vehicle-profile', profileId], (signal) => vehicleApi.get(profileId, signal));
  const investors = usePortalQuery(['car-management-investors'], (signal) => investorApi.list('', signal));
  const profile = details.data?.profile;
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [ownershipOutcome, setOwnershipOutcome] = useState(null);
  const [ownershipBusy, setOwnershipBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [tab, setTab] = useTabParam(['profile', 'ownership', 'costs', 'earnings']);
  const openEdit = () => {
    setFields({
      make: profile.Make || profile.make || '', model: profile.Model || profile.model || '',
      year: profile.year == null ? '' : String(profile.year), description: profile.description || '',
    }); setEditing(true); setError('');
  };
  const save = async (event) => {
    event.preventDefault(); setError(''); setMessage('');
    const year = fields.year ? Number(fields.year) : null;
    if (fields.year && (!Number.isInteger(year) || year < 1886 || year > 2100)) { setError('Year must be an integer from 1886 to 2100.'); return; }
    if (busyRef.current || ownershipBusy) return;
    busyRef.current = true; setBusy(true);
    try {
      const next = {
        make: fields.make.trim() || null, model: fields.model.trim() || null,
        year, description: fields.description.trim() || null,
      };
      const current = {
        make: profile.Make || profile.make || null, model: profile.Model || profile.model || null,
        year: profile.year ?? null, description: profile.description || null,
      };
      const patch = Object.fromEntries(Object.entries(next).filter(([key, value]) => value !== current[key]));
      if (!Object.keys(patch).length) {
        setEditing(false); setMessage('No profile changes to save.'); return;
      }
      const updated = await vehicleApi.update(profileId, patch);
      if (updated.id !== profileId) throw new Error('The server returned a changed profile ID; stop and reload before proceeding.');
      setMessage('Car profile updated. Its profile ID and linked photos remain unchanged.');
      setEditing(false); invalidatePortal();
      details.refresh();
    } catch (requestError) { setError(requestError.message || 'Unable to update car profile.'); }
    finally { busyRef.current = false; setBusy(false); }
  };
  return <main className="portal"><header className="portal-header">
    <p><Link to="/admin/vehicles">← Car management</Link></p>
    <h1>{profile ? label(profile) : 'Car profile'}</h1><p>Profile ID: {profileId}</p>
  </header><QueryStatus query={details} /><QueryStatus query={investors} />
    {error && <p className="portal-error" role="alert">{error}</p>}
    {message && <p className="portal-success" role="status">{message}</p>}
    {ownershipOutcome && <p className={ownershipOutcome.error ? 'portal-error' : 'portal-success'}
      role={ownershipOutcome.error ? 'alert' : 'status'}>{ownershipOutcome.text}</p>}
    {profile && <Tabs label="Car management sections" value={tab} onChange={setTab} tabs={[
      { id: 'profile', label: 'Car Profile', content: <section className="portal-card"><div className="portal-actions"><h2>Car profile</h2>
      {!editing && <button disabled={ownershipBusy} onClick={openEdit}>Edit metadata</button>}</div>
      {editing ? <form className="portal-form" onSubmit={save}>
        <label>Plate number (immutable)<input value={label(profile)} readOnly /></label>
        <label>Make<input value={fields.make} onChange={(event) => setFields({ ...fields, make: event.target.value })} /></label>
        <label>Model<input value={fields.model} onChange={(event) => setFields({ ...fields, model: event.target.value })} /></label>
        <label>Year<input type="number" min="1886" max="2100" value={fields.year} onChange={(event) => setFields({ ...fields, year: event.target.value })} /></label>
        <label>Description<textarea value={fields.description} onChange={(event) => setFields({ ...fields, description: event.target.value })} /></label>
        <div className="portal-actions"><button disabled={busy || ownershipBusy}>{busy ? 'Saving...' : 'Save metadata'}</button>
          <button type="button" disabled={busy} onClick={() => setEditing(false)}>Cancel</button></div>
      </form> : <dl>
        <dt>Plate number</dt><dd>{label(profile)}</dd><dt>Make / model</dt><dd>{[profile.Make || profile.make, profile.Model || profile.model].filter(Boolean).join(' ') || 'Not provided'}</dd>
        <dt>Year</dt><dd>{profile.year || 'Not provided'}</dd><dt>Description</dt><dd>{profile.description || 'Not provided'}</dd>
      </dl>}
    </section> },
      { id: 'ownership', label: 'Ownership', content: <OwnershipPanel key={`ownership-${profileId}`} car={profile} investors={investors.data || []}
        disabled={details.loading || investors.loading || !!investors.error || busy || ownershipBusy}
        onOutcome={setOwnershipOutcome} onBusyChange={setOwnershipBusy} /> },
      { id: 'costs', label: 'Additional costs', content: <CostManager key={profileId} profileId={profileId} /> },
      { id: 'earnings', label: 'Earnings & Costs', content: <VehicleEarnings vehicleId={profileId} /> },
    ]} />}
  </main>;
}

function OwnershipPanel({ car, investors, disabled, onOutcome, onBusyChange }) {
  const [investorId, setInvestorId] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const target = investors.find((investor) => investor.id === investorId);
  const ownerId = car.Owner_investor_id;
  const ownerName = investors.find((investor) => investor.id === ownerId)?.name || ownerId;
  const perform = async () => {
    if (busyRef.current || !confirm || disabled) return;
    busyRef.current = true; setBusy(true); onBusyChange(true); onOutcome(null);
    try {
      const updated = confirm === 'assign' ? await ownershipApi.assign(car.id, { investor_id: investorId }) : await ownershipApi.release(car.id);
      if (updated.id !== car.id || updated.Owner_investor_id !== (confirm === 'assign' ? investorId : null)) {
        throw new Error('Ownership change could not be verified. Reload the car before retrying.');
      }
      onOutcome({ error: false, text: confirm === 'assign' ? 'Current owner updated.' : 'Ownership released. The car, photos, costs, and imported statements remain.' });
    } catch (requestError) {
      const uncertain = !requestError.status || requestError.status >= 500;
      onOutcome({ error: true, text: `${requestError.message || 'Unable to update ownership.'}${uncertain ? ' The write may have applied; reload the car before retrying.' : ''}` });
    } finally { busyRef.current = false; setBusy(false); onBusyChange(false); setConfirm(''); invalidatePortal(); }
  };
  return <section className="portal-card"><h2>Ownership</h2>
    <p>Current owner: {ownerName || 'Unassigned'}</p>
    <p className="portal-muted">Ownership has no start or release date. All manual costs, including past months, are charged to the current owner. Imported statements retain the investor recorded at import.</p>
    <form className="portal-form" onSubmit={(event) => { event.preventDefault(); setConfirm('assign'); }}>
      <label>Investor<select required disabled={busy || disabled} value={investorId} onChange={(event) => { setInvestorId(event.target.value); setConfirm(''); }}>
        <option value="">Select investor</option>{investors.map((investor) => <option key={investor.id} value={investor.id}>{investor.name}</option>)}
      </select></label>
      <div className="portal-actions"><button disabled={busy || disabled || !target}>Review assignment / transfer</button></div>
    </form>
    <button type="button" disabled={busy || disabled || !ownerId} onClick={() => setConfirm('release')}>Release ownership</button>
    {confirm && <div className="portal-warning" role="region" aria-label="Ownership confirmation">
      <p>{confirm === 'assign' ? `${ownerId ? `Replace ${ownerName} with ${target?.name} as the owner of ${label(car)}` : `Assign ${label(car)} to ${target?.name}`}? All manual costs will be attributed to this owner.` :
        `Release ownership of ${label(car)} from ${ownerName}? The car, photos, costs, and imported statements remain. Manual costs will no longer be charged to an investor.`}</p>
      <div className="portal-actions"><button type="button" disabled={busy || disabled} onClick={perform}>{busy ? 'Saving...' : 'Confirm ownership change'}</button>
        <button type="button" disabled={busy} onClick={() => setConfirm('')}>Cancel</button></div>
    </div>}
  </section>;
}
